import { type FormEvent, Fragment, type ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { type SearchResultDTO, type SearchType } from "@justone/shared";
import { errorMessage, http } from "../api/http";
import { useChannels, useConversations, useMe, useUserMap } from "../api/queries";
import { useWikiSpaces } from "../api/wiki";
import { formatBytes } from "../components/Attachments";
import { TaskRow } from "../components/TaskBits";
import { Avatar, EmptyState, Skeleton } from "../components/ui";
import { formatDay, formatTime, messagePath } from "../lib/format";
import { dmTitle } from "../lib/room";
import { wikiPagePath } from "../lib/wiki";
import { PageShell } from "./OtherPages";
import { useUpdateParams } from "../lib/params";

type Tab = "all" | SearchType;
const TABS: [Tab, string][] = [
  ["all", "전체"],
  ["message", "메시지"],
  ["task", "할일"],
  ["wiki", "위키"],
  ["file", "파일"],
];

/** 검색어 낱말을 굵게 칠한다 */
export function Highlight({ text, terms }: { text: string; terms: string[] }) {
  const words = terms.filter(Boolean).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!words.length) return <>{text}</>;
  const re = new RegExp(`(${words.join("|")})`, "gi");
  const parts = text.split(re);
  return (
    <>
      {parts.map((p, i) => (i % 2 === 1 ? <mark key={i}>{p}</mark> : <Fragment key={i}>{p}</Fragment>))}
    </>
  );
}

export function SearchPage() {
  const [params] = useSearchParams();
  const updateParams = useUpdateParams();
  const q = params.get("q") ?? "";
  const tab = (params.get("type") as Tab) || "all";
  const [input, setInput] = useState(q);
  useEffect(() => setInput(q), [q]);

  const query = useInfiniteQuery({
    queryKey: ["search", q, tab],
    queryFn: ({ pageParam }) => http.get<SearchResultDTO>(`/api/search?q=${encodeURIComponent(q)}${tab !== "all" ? `&type=${tab}` : ""}&offset=${pageParam}`),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      if (tab === "all") return undefined;
      const key = ({ message: "messages", task: "tasks", wiki: "wiki", file: "files" } as const)[last.query.type ?? (tab as SearchType)];
      const loaded = pages.reduce((n, p) => n + p[key].items.length, 0);
      return loaded < last[key].total ? loaded : undefined;
    },
    enabled: q.trim().length > 0,
    staleTime: 10_000,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    updateParams((p) => {
      p.set("q", input.trim());
    });
  };
  const setQuery = (next: string) => {
    setInput(next);
    updateParams((p) => {
      p.set("q", next);
    });
  };
  const setTab = (t: Tab) =>
    updateParams((p) => {
      if (t === "all") p.delete("type");
      else p.set("type", t);
    });

  const pages = query.data?.pages ?? [];
  const first = pages[0];
  const terms = first?.query.terms ?? [];
  const merged = first && {
    messages: pages.flatMap((p) => p.messages.items),
    tasks: pages.flatMap((p) => p.tasks.items),
    wiki: pages.flatMap((p) => p.wiki.items),
    files: pages.flatMap((p) => p.files.items),
  };
  const total = first ? first.messages.total + first.tasks.total + first.wiki.total + first.files.total : 0;
  const counts: Record<Tab, number> = { all: total, message: first?.messages.total ?? 0, task: first?.tasks.total ?? 0, wiki: first?.wiki.total ?? 0, file: first?.files.total ?? 0 };
  const shown = first?.query.type ?? tab;

  return (
    <PageShell title="검색">
      <form className="search-form" onSubmit={submit} role="search">
        <label htmlFor="search-page-input" className="sr-only">
          검색어
        </label>
        <input id="search-page-input" className="input" type="search" value={input} onChange={(e) => setInput(e.target.value)} placeholder="메시지·할일·위키·파일 찾기" autoFocus enterKeyHint="search" />
        <button type="submit" className="btn btn-primary">
          찾기
        </button>
      </form>
      <p className="search-help">
        <code>in:#채널</code> <code>from:@사람</code> <code>after:2026-10-01</code> <code>before:2026-10-31</code> <code>is:task</code> <code>is:wiki</code> <code>"정확한 문구"</code>
      </p>
      {q && (
        <div className="seg search-tabs" role="tablist" aria-label="결과 종류">
          {TABS.map(([t, label]) => (
            <button key={t} type="button" role="tab" aria-selected={shown === t} className={shown === t ? "on" : ""} onClick={() => setTab(t)} disabled={!!first?.query.type && t !== first.query.type}>
              {label} {first && <span className="count">{counts[t]}</span>}
            </button>
          ))}
        </div>
      )}
      {!q ? (
        <EmptyState icon="search" title="무엇을 찾을까요?">
          낱말을 여러 개 쓰면 모두 들어 있는 것만 찾습니다. 위의 조건을 붙이면 채널·사람·날짜로 좁힙니다.
        </EmptyState>
      ) : query.isPending ? (
        <Skeleton kind="messages" rows={4} />
      ) : query.isError ? (
        <p className="empty">찾지 못했습니다. {errorMessage(query.error)}</p>
      ) : first?.notice ? (
        <EmptyState icon="search" title={first.notice} action={<RetryWithout q={q} parsed={first.query} onSearch={setQuery} />} />
      ) : total === 0 ? (
        <EmptyState icon="search" title={`"${q}"에 맞는 결과가 없습니다`} action={<RetryWithout q={q} parsed={first?.query} onSearch={setQuery} />}>
          낱말을 줄이거나 다른 말로 찾아 보세요. 위키는 지금 내용만 찾습니다.
        </EmptyState>
      ) : (
        merged && (
          <div className="search-results">
            {(shown === "all" || shown === "message") && counts.message > 0 && (
              <Section title="메시지" count={counts.message} more={shown === "all" ? () => setTab("message") : undefined}>
                {merged.messages.map((m) => (
                  <MessageHit key={m.id} hit={m} terms={terms} />
                ))}
              </Section>
            )}
            {(shown === "all" || shown === "task") && counts.task > 0 && (
              <Section title="할일" count={counts.task} more={shown === "all" ? () => setTab("task") : undefined}>
                <TaskHits tasks={merged.tasks} />
              </Section>
            )}
            {(shown === "all" || shown === "wiki") && counts.wiki > 0 && (
              <Section title="위키" count={counts.wiki} more={shown === "all" ? () => setTab("wiki") : undefined}>
                {merged.wiki.map((w) => (
                  <WikiHit key={w.id} hit={w} terms={terms} />
                ))}
              </Section>
            )}
            {(shown === "all" || shown === "file") && counts.file > 0 && (
              <Section title="파일" count={counts.file} more={shown === "all" ? () => setTab("file") : undefined}>
                {merged.files.map((f) => (
                  <FileHit key={f.id} hit={f} terms={terms} />
                ))}
              </Section>
            )}
            {query.hasNextPage && (
              <button type="button" className="btn" onClick={() => void query.fetchNextPage()} disabled={query.isFetchingNextPage}>
                더 보기
              </button>
            )}
          </div>
        )
      )}
    </PageShell>
  );
}

/** 조건(in:·from:·before:·after:·is:)을 빼고 다시 찾기 */
function RetryWithout({ q, parsed, onSearch }: { q: string; parsed: SearchResultDTO["query"] | undefined; onSearch: (q: string) => void }) {
  const hasConditions = !!parsed && (!!parsed.channel || !!parsed.from || !!parsed.before || !!parsed.after || !!parsed.type);
  if (!hasConditions || !parsed) return null;
  const words = parsed.terms.map((t) => (/\s/.test(t) ? `"${t}"` : t)).join(" ");
  if (!words || words === q) return null;
  return (
    <button type="button" className="btn btn-sm" onClick={() => onSearch(words)}>
      조건 빼고 "{words}"만 찾기
    </button>
  );
}

function Section({ title, count, more, children }: { title: string; count: number; more?: () => void; children: ReactNode }) {
  return (
    <section className="search-section" aria-label={title}>
      <h2>
        {title} <span className="count">{count}</span>
        {more && count > 5 && (
          <button type="button" className="btn btn-sm btn-quiet" onClick={more}>
            모두 보기
          </button>
        )}
      </h2>
      <div className="search-list">{children}</div>
    </section>
  );
}

/** 메시지가 있는 곳: #채널 또는 DM 상대 */
function usePlace() {
  const { data: channels = [] } = useChannels();
  const { data: conversations = [] } = useConversations();
  const users = useUserMap();
  const me = useMe();
  return (channelId: number | null, conversationId: number | null) => {
    if (channelId !== null) return `#${channels.find((c) => c.id === channelId)?.name ?? "채널"}`;
    const conv = conversations.find((c) => c.id === conversationId);
    return conv ? `DM · ${dmTitle(conv, users, me.id)}` : "DM";
  };
}

function MessageHit({ hit, terms }: { hit: SearchResultDTO["messages"]["items"][number]; terms: string[] }) {
  const users = useUserMap();
  const place = usePlace();
  const author = users.get(hit.authorId ?? 0);
  return (
    <Link className="search-hit" to={messagePath(hit, hit.id, hit.parentId)}>
      <Avatar user={author} size={28} />
      <span className="grow">
        <span className="hit-head">
          <strong>{author?.displayName ?? "알 수 없음"}</strong>
          <span>{place(hit.channelId, hit.conversationId)}</span>
          {hit.parentId && <span>스레드</span>}
          <time dateTime={hit.createdAt}>
            {formatDay(hit.createdAt)} {formatTime(hit.createdAt)}
          </time>
        </span>
        <span className="hit-text">
          <Highlight text={hit.snippet} terms={terms} />
        </span>
      </span>
    </Link>
  );
}

function TaskHits({ tasks }: { tasks: SearchResultDTO["tasks"]["items"] }) {
  const users = useUserMap();
  return (
    <div className="task-list">
      {tasks.map((t) => (
        <TaskRow key={t.id} task={t} users={users} />
      ))}
    </div>
  );
}

function WikiHit({ hit, terms }: { hit: SearchResultDTO["wiki"]["items"][number]; terms: string[] }) {
  const { data: spaces = [] } = useWikiSpaces();
  const space = spaces.find((s) => s.id === hit.spaceId);
  return (
    <Link className="search-hit" to={wikiPagePath(space?.channelId ?? null, hit.id)}>
      <span className="hit-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2V5ZM4 19a2 2 0 0 1 2-2h13M8 7h7" />
        </svg>
      </span>
      <span className="grow">
        <span className="hit-head">
          <strong>
            <Highlight text={hit.title} terms={terms} />
          </strong>
          <span>{space?.name ?? "위키"}</span>
          {hit.status === "draft" && <span className="draft-tag">초안</span>}
          <time dateTime={hit.updatedAt}>{formatDay(hit.updatedAt)}</time>
        </span>
        {hit.snippet && (
          <span className="hit-text">
            <Highlight text={hit.snippet} terms={terms} />
          </span>
        )}
      </span>
    </Link>
  );
}

function FileHit({ hit, terms }: { hit: SearchResultDTO["files"]["items"][number]; terms: string[] }) {
  const users = useUserMap();
  const place = usePlace();
  const navigate = useNavigate();
  return (
    <div className="search-hit" role="link" tabIndex={0} onClick={() => navigate(messagePath(hit, hit.messageId, hit.parentId))} onKeyDown={(e) => e.key === "Enter" && navigate(messagePath(hit, hit.messageId, hit.parentId))}>
      {hit.thumbUrl ? <img className="hit-thumb" src={hit.thumbUrl} alt="" width={40} height={40} /> : <span className="hit-icon ext">{hit.filename.split(".").pop()?.slice(0, 4) ?? "파일"}</span>}
      <span className="grow">
        <span className="hit-head">
          <strong>
            <Highlight text={hit.filename} terms={terms} />
          </strong>
          <span>{formatBytes(hit.size)}</span>
        </span>
        <span className="hit-text">
          {users.get(hit.authorId ?? 0)?.displayName ?? "알 수 없음"} · {place(hit.channelId, hit.conversationId)} · {formatDay(hit.createdAt)}
        </span>
      </span>
      <a className="btn btn-sm" href={`${hit.url}?download`} onClick={(e) => e.stopPropagation()} download>
        받기
      </a>
    </div>
  );
}
